import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, MapPin, Flag, Path, Clock, CircleNotch, ArrowRight } from "@phosphor-icons/react";

const Row = ({ label, value, testid, strong }) => (
  <div className="flex items-start justify-between gap-4 py-2.5">
    <span className="cfg-body text-[13px] text-white/50">{label}</span>
    <span data-testid={testid} className={`cfg-body text-[13px] text-right ${strong ? "text-[#D4AF37] font-semibold" : "text-white/90"}`}>{value}</span>
  </div>
);

export const BookingSummary = ({ t, vehicle, image, pax, pickup, dropoff, quote, loading, onBook, booking }) => {
  const cur = "€";
  const rest = quote ? (quote.total - quote.deposit).toFixed(2) : null;

  return (
    <div className="cfg rounded-[24px] overflow-hidden bg-[#071B2F] shadow-[0_40px_90px_-40px_rgba(7,27,47,0.7)] sticky top-28" data-testid="booking-summary">
      <div className="relative h-40 overflow-hidden bg-[#0a2338]">
        <AnimatePresence mode="wait">
          <motion.img
            key={vehicle?.id || "none"}
            src={image}
            alt={vehicle?.model}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full h-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-[#071B2F] via-transparent to-transparent" />
        <div className="absolute bottom-4 left-6">
          <p className="cfg-body text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]">{t.summaryTitle}</p>
          <h3 className="cfg-display text-2xl text-white leading-tight" data-testid="summary-vehicle-name">{vehicle?.name}</h3>
        </div>
      </div>

      <div className="p-6">
        <div className="divide-y divide-white/8">
          <Row label={t.sVehicle} value={vehicle?.model || "—"} />
          <Row label={t.sPax} value={pax} />
          <Row label={t.sPickup} value={pickup ? <span className="line-clamp-1 max-w-[160px] inline-block align-bottom">{pickup}</span> : "—"} testid="summary-pickup" />
          <Row label={t.sDest} value={dropoff ? <span className="line-clamp-1 max-w-[160px] inline-block align-bottom">{dropoff}</span> : "—"} testid="summary-dropoff" />
          <Row label={t.sDistance} value={quote?.distance_km ? `${quote.distance_km} km` : "—"} testid="summary-distance" />
          <Row label={t.sDuration} value={quote?.duration_min ? `${quote.duration_min} min` : "—"} />
        </div>

        <div className="mt-4 rounded-2xl bg-white/5 p-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-4 text-white/50 cfg-body text-sm">
              <CircleNotch size={18} className="animate-spin" /> …
            </div>
          ) : quote ? (
            <>
              <div className="flex items-center justify-between">
                <span className="cfg-body text-[13px] text-white/60">{t.sFare}</span>
                <span className="cfg-display text-2xl text-white" data-testid="summary-fare">{cur}{quote.total.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/8">
                <span className="cfg-body text-[13px] text-[#D4AF37]">{t.sDeposit}</span>
                <span className="cfg-display text-xl text-[#D4AF37]" data-testid="summary-deposit">{cur}{quote.deposit.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="cfg-body text-[12px] text-white/40">{t.sRemaining}</span>
                <span className="cfg-body text-[13px] text-white/60">{cur}{rest}</span>
              </div>
            </>
          ) : (
            <p className="cfg-body text-[13px] text-white/45 text-center py-2">{t.fillRoute}</p>
          )}
        </div>

        <motion.button
          type="button"
          onClick={onBook}
          disabled={booking}
          data-testid="book-my-ride-btn"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="cfg-body w-full mt-5 inline-flex items-center justify-center gap-3 rounded-2xl bg-[#D4AF37] hover:bg-[#c6a02f] disabled:opacity-60 text-[#071B2F] font-semibold tracking-widest uppercase text-[13px] py-4 shadow-[0_10px_30px_-8px_rgba(212,175,55,0.6)] transition-colors"
        >
          {booking ? t.booking : t.book}
          {!booking && <ArrowRight weight="bold" size={18} />}
        </motion.button>
      </div>
    </div>
  );
};
